/**
 * FlexibleMediaSection — Media + Gradient editor for FlexibleLayout Media sections
 *
 * Combines media upload (Image/Video) from V2MediaSection with gradient controls
 * from V2PaywallMediaSection, adapted for FlexibleLayout paths.
 *
 * Key difference from V2PaywallMediaSection:
 *   Paywall gradient: content.paywall.bodyAppearance.overlay
 *   Flexible gradient: content.featuredMedia.appearance.gradient (FeaturedMediaStyle.gradient)
 *
 * No heroAnchor (image position) — that's paywall-specific.
 *
 * Data flow through proxy adapter:
 *   onChange('content.featuredMedia.appearance.gradient.heightPercentage', 60)
 *   → adapter strips 'content.' → matches prefix 'featuredMedia'
 *   → innerPath 'appearance.gradient.heightPercentage'
 *   → deep-clones section.mediaContent, setNestedValue, writes whole sections array
 */

import React, { useCallback, useState, useMemo, useRef } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Typography,
  Divider,
  Slider,
  Collapse,
  IconButton,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { SAccordion } from '@modules/ui-components';
import { ImageUploadElement } from '../../../factories/elements/ImageUploadElement';
import { VideoUploadElement } from '../../../factories/elements/VideoUploadElement';
import { StyleFieldRow } from '../../shared/styleFields';
import { EditorGuideProvider } from '../../../contexts';
import { OnboardingMediaUploadService } from '@modules/firebase-core/services/OnboardingMediaUploadService';
import { toV2AssetReference, toV2VideoAssetReference, readAssetUrl, type V2AssetReference } from '@modules/reactFlow/utils/v2AssetUtils';
import { useMultiLocaleUpload } from '../../../hooks/useMultiLocaleUpload';
import { MultiLocaleUploadDialog } from '../../shared/MultiLocaleUploadDialog';
import {
  V2_DEFAULT_MEDIA_STRUCTURE,
  V2_MEDIA_KIND_OPTIONS,
  V2_DEFAULT_IMAGE_CONTENT,
  V2_DEFAULT_VIDEO_CONTENT,
  V2_FLEXIBLE_GRADIENT_STRUCTURE,
  V2_FLEXIBLE_GRADIENT_COLOR_CONFIG,
  V2_MEDIA_APPEARANCE_FIELDS,
  V2_MEDIA_REPEAT_FIELD,
  type V2MediaKind,
} from '../../../config/v2FlexibleMediaConfig';
import { withScreenEditorFieldGuide, withScreenEditorFieldGuides } from '../../../config/screenEditorFieldGuideResolver';
import { SECTION_MARGIN_BOTTOM, FIELD_MARGIN_BOTTOM, FIELD_GAP, FIELD_STYLE_GRID_GAP } from '../../../config/ui';
import {
  checkboxSx, checkboxIconSx, checkboxCheckedIconSx,
  expandedToggleButtonSx, expandedToggleButtonActiveSx, expandedSectionSx,
} from '../../../config/editorFieldStyles';
import { getValueByPath, isFieldEnabled } from '../../../utils/v2FieldOperations';
import { getSectionTestId } from '../../../utils/testIdUtils';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

export interface FlexibleMediaSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  projectId?: string;
  onboardingId?: string;
  versionId?: string;
  availableLocales?: string[];
  /** Override accordion title (used by FlexibleLayout for section labels) */
  sectionTitle?: string;
}

export const FlexibleMediaSection: React.FC<FlexibleMediaSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  projectId,
  onboardingId,
  versionId,
  availableLocales = [],
  sectionTitle,
}) => {
  const [uploading, setUploading] = useState(false);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [showLocaleDialog, setShowLocaleDialog] = useState(false);
  const [appearanceExpanded, setAppearanceExpanded] = useState(false);
  const [pendingUploadAssetUrl, setPendingUploadAssetUrl] = useState<V2AssetReference | null>(null);
  const [pendingMediaType, setPendingMediaType] = useState<'image' | 'video'>('image');
  const lastUploadedAssetUrlRef = useRef<V2AssetReference | null>(null);

  const { savedPreference, shouldShowDialog, savePreference } = useMultiLocaleUpload();
  const uploadService = useMemo(() => new OnboardingMediaUploadService(), []);

  // ── Media state ──
  const isMediaEnabled = useMemo(
    () => isFieldEnabled(step, 'content.featuredMedia'),
    [step],
  );

  const mediaKind = useMemo<V2MediaKind>(() => {
    const kind = getValueByPath<string>(step, 'content.featuredMedia.kind');
    return (kind as V2MediaKind) ?? 'Image';
  }, [step]);

  const imageUrl = useMemo(() => {
    const asset = getValueByPath<Record<string, string>>(
      step,
      `content.featuredMedia.content.image.translations.${locale}`,
    );
    return readAssetUrl(asset);
  }, [step, locale]);

  const videoUrl = useMemo(() => {
    const asset = getValueByPath<Record<string, string>>(
      step,
      `content.featuredMedia.content.video.translations.${locale}`,
    );
    return readAssetUrl(asset);
  }, [step, locale]);

  // ── Gradient state (FlexibleLayout path: content.featuredMedia.appearance.gradient) ──
  const isGradientEnabled = useMemo(() => {
    const gradient = getValueByPath(step, 'content.featuredMedia.appearance.gradient');
    return gradient !== null && gradient !== undefined;
  }, [step]);

  const gradientHeight = useMemo(() => {
    return getValueByPath<number>(step, 'content.featuredMedia.appearance.gradient.heightPercentage') ?? 50;
  }, [step]);

  const gradientColor = useMemo(() => {
    const colors = getValueByPath<string[]>(step, 'content.featuredMedia.appearance.gradient.colors');
    return colors?.[0] ?? '#000000';
  }, [step]);

  // ── Media handlers ──
  const toggleFieldExpansion = useCallback((path: string) => {
    setExpandedFields((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const handleMediaEnabledChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked;
      onChange('content.featuredMedia', enabled ? V2_DEFAULT_MEDIA_STRUCTURE : null);
    },
    [onChange],
  );

  const handleKindChange = useCallback(
    (event: SelectChangeEvent) => {
      const newKind = event.target.value as V2MediaKind;
      const currentMedia = getValueByPath<Record<string, unknown>>(step, 'content.featuredMedia');
      const updatedMedia = {
        ...currentMedia,
        kind: newKind,
        content: newKind === 'Image' ? V2_DEFAULT_IMAGE_CONTENT : V2_DEFAULT_VIDEO_CONTENT,
      };
      onChange('content.featuredMedia', updatedMedia);
    },
    [onChange, step],
  );

  const handleImageChange = useCallback(
    (value: string | null) => {
      if (value) {
        const assetRef = lastUploadedAssetUrlRef.current ?? { original: value, small: value };
        onChange(`content.featuredMedia.content.image.translations.${locale}`, assetRef);
        lastUploadedAssetUrlRef.current = null;
      } else {
        onChange(`content.featuredMedia.content.image.translations.${locale}`, null);
      }
    },
    [onChange, locale],
  );

  const handleVideoChange = useCallback(
    (value: string | null) => {
      if (value) {
        const assetRef = lastUploadedAssetUrlRef.current ?? { original: value, small: value };
        onChange(`content.featuredMedia.content.video.translations.${locale}`, assetRef);
        lastUploadedAssetUrlRef.current = null;
      } else {
        onChange(`content.featuredMedia.content.video.translations.${locale}`, null);
      }
    },
    [onChange, locale],
  );

  const handleImageUpload = useCallback(
    async (file: File): Promise<string> => {
      setUploading(true);
      try {
        const result = await uploadService.uploadImage(file, {
          projectId: projectId!,
          onboardingId: onboardingId!,
          versionId: versionId!,
          locale,
        });
        const v2Asset = toV2AssetReference(result.assetUrl);
        lastUploadedAssetUrlRef.current = v2Asset;

        if (shouldShowDialog(availableLocales)) {
          setPendingUploadAssetUrl(v2Asset);
          setPendingMediaType('image');
          setShowLocaleDialog(true);
        } else if (savedPreference === 'all' && availableLocales.length > 1) {
          for (const loc of availableLocales) {
            onChange(`content.featuredMedia.content.image.translations.${loc}`, v2Asset);
          }
        }
        return result.assetUrl.origin;
      } finally {
        setUploading(false);
      }
    },
    [uploadService, projectId, onboardingId, versionId, locale, availableLocales, shouldShowDialog, savedPreference, onChange],
  );

  const handleVideoUpload = useCallback(
    async (file: File, onProgress?: (fraction: number) => void): Promise<string> => {
      setUploading(true);
      try {
        const result = await uploadService.uploadVideo(file, {
          projectId: projectId!,
          onboardingId: onboardingId!,
          versionId: versionId!,
          locale,
        }, { onProgress });
        const v2Asset = toV2AssetReference(result.assetUrl);
        lastUploadedAssetUrlRef.current = v2Asset;

        if (shouldShowDialog(availableLocales)) {
          setPendingUploadAssetUrl(v2Asset);
          setPendingMediaType('video');
          setShowLocaleDialog(true);
        } else if (savedPreference === 'all' && availableLocales.length > 1) {
          for (const loc of availableLocales) {
            onChange(`content.featuredMedia.content.video.translations.${loc}`, toV2VideoAssetReference(result.assetUrl));
          }
        }
        return result.assetUrl.origin;
      } finally {
        setUploading(false);
      }
    },
    [uploadService, projectId, onboardingId, versionId, locale, availableLocales, shouldShowDialog, savedPreference, onChange],
  );

  const handleStyleChange = useCallback(
    (stylePath: string, value: unknown) => onChange(stylePath, value),
    [onChange],
  );

  // ── Gradient handlers (FlexibleLayout paths) ──
  const handleGradientToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked;
      onChange('content.featuredMedia.appearance.gradient', enabled ? V2_FLEXIBLE_GRADIENT_STRUCTURE : null);
    },
    [onChange],
  );

  const handleGradientHeightChange = useCallback(
    (_event: Event, value: number | number[]) => {
      const newHeight = typeof value === 'number' ? value : value[0];
      onChange('content.featuredMedia.appearance.gradient.heightPercentage', newHeight);
    },
    [onChange],
  );

  const handleGradientColorChange = useCallback(
    (value: unknown) => {
      const color = value as string;
      const currentColors = getValueByPath<string[]>(step, 'content.featuredMedia.appearance.gradient.colors') || ['#000000', '#00000000'];
      onChange('content.featuredMedia.appearance.gradient.colors', [color, currentColors[1]]);
    },
    [step, onChange],
  );

  // ── Multi-locale dialog ──
  const applyMediaToLocale = useCallback(
    (assetRef: V2AssetReference, targetLocale: string, mediaType: 'image' | 'video') => {
      const contentKey = mediaType === 'image' ? 'image' : 'video';
      onChange(`content.featuredMedia.content.${contentKey}.translations.${targetLocale}`, assetRef);
    },
    [onChange],
  );

  const handleApplyToAllLocales = useCallback(
    (rememberChoice: boolean) => {
      if (!pendingUploadAssetUrl) return;
      if (rememberChoice) savePreference('all');
      for (const loc of availableLocales) {
        applyMediaToLocale(pendingUploadAssetUrl, loc, pendingMediaType);
      }
      setShowLocaleDialog(false);
      setPendingUploadAssetUrl(null);
    },
    [pendingUploadAssetUrl, availableLocales, pendingMediaType, applyMediaToLocale, savePreference],
  );

  const handleApplyToCurrentLocale = useCallback(
    (rememberChoice: boolean) => {
      if (!pendingUploadAssetUrl) return;
      if (rememberChoice) savePreference('current');
      applyMediaToLocale(pendingUploadAssetUrl, locale, pendingMediaType);
      setShowLocaleDialog(false);
      setPendingUploadAssetUrl(null);
    },
    [pendingUploadAssetUrl, locale, pendingMediaType, applyMediaToLocale, savePreference],
  );

  const handleCancelLocaleDialog = useCallback(() => {
    setShowLocaleDialog(false);
    setPendingUploadAssetUrl(null);
  }, []);

  // Placeholder configs for upload elements
  const mediaImageConfig = useMemo(() => ({
    path: 'content.featuredMedia.content.image',
    label: 'Image',
    type: 'imageUpload' as const,
    defaultValue: '',
    disableBehavior: 'null' as const,
  }), []);

  const mediaVideoConfig = useMemo(() => ({
    path: 'content.featuredMedia.content.video',
    label: 'Video',
    type: 'videoUpload' as const,
    defaultValue: '',
    disableBehavior: 'null' as const,
  }), []);

  return (
    <SAccordion
      title={sectionTitle ?? 'Media'}
      collapsible
      defaultExpanded={defaultExpanded}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId('flexible-media')}
    >
      <EditorGuideProvider>
        {/* Enable Media */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP, mb: FIELD_MARGIN_BOTTOM }}>
          <Checkbox
            checked={isMediaEnabled}
            onChange={handleMediaEnabledChange}
            disabled={disabled}
            size="small"
            sx={checkboxSx}
            icon={<Box sx={checkboxIconSx} />}
            checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
          />
          <Typography variant="body2" color="text.secondary">
            Enable
          </Typography>
        </Box>

        {/* Kind Selector + Appearance toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: FIELD_MARGIN_BOTTOM }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={mediaKind}
              onChange={handleKindChange}
              disabled={disabled || !isMediaEnabled}
            >
              {V2_MEDIA_KIND_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            size="small"
            onClick={() => setAppearanceExpanded(prev => !prev)}
            disabled={disabled || !isMediaEnabled}
            sx={appearanceExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Appearance settings (scaleMode, height%, cornerRadius, repeat) */}
        <Collapse in={appearanceExpanded && isMediaEnabled} timeout="auto" unmountOnExit>
          <Box sx={expandedSectionSx}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: FIELD_STYLE_GRID_GAP }}>
              {withScreenEditorFieldGuides(V2_MEDIA_APPEARANCE_FIELDS, 'flexibleSections').map((fieldConfig) => (
                <StyleFieldRow
                  key={fieldConfig.path}
                  config={fieldConfig}
                  value={getValueByPath(step as unknown as Record<string, unknown>, fieldConfig.path)}
                  onChange={(value) => onChange(fieldConfig.path, value)}
                  disabled={disabled}
                />
              ))}
              {mediaKind === 'Video' && (
                <StyleFieldRow
                  config={withScreenEditorFieldGuide(V2_MEDIA_REPEAT_FIELD, 'flexibleSections')}
                  value={getValueByPath(step as unknown as Record<string, unknown>, V2_MEDIA_REPEAT_FIELD.path)}
                  onChange={(value) => onChange(V2_MEDIA_REPEAT_FIELD.path, value)}
                  disabled={disabled}
                />
              )}
            </Box>
          </Box>
        </Collapse>

        {/* Image upload */}
        {mediaKind === 'Image' && (
          <Box>
            <ImageUploadElement
              fieldConfig={mediaImageConfig}
              value={imageUrl}
              templateValue={null}
              enabled={true}
              locale={locale}
              onChange={handleImageChange}
              onEnabledChange={() => {}}
              onUpload={handleImageUpload}
              disabled={disabled || uploading || !isMediaEnabled}
              hideCheckbox={true}
              expandedStyles={expandedFields.has('media-image')}
              onStylesExpandToggle={() => toggleFieldExpansion('media-image')}
              styleValues={{}}
              onStyleChange={handleStyleChange}
            />
          </Box>
        )}

        {/* Video upload */}
        {mediaKind === 'Video' && (
          <Box>
            <VideoUploadElement
              fieldConfig={mediaVideoConfig}
              value={videoUrl}
              templateValue={null}
              enabled={true}
              locale={locale}
              onChange={handleVideoChange}
              onEnabledChange={() => {}}
              onUpload={handleVideoUpload}
              disabled={disabled || uploading || !isMediaEnabled}
              hideCheckbox={true}
              expandedStyles={expandedFields.has('media-video')}
              onStylesExpandToggle={() => toggleFieldExpansion('media-video')}
              styleValues={{}}
              onStyleChange={handleStyleChange}
            />
          </Box>
        )}

        {/* Gradient Section */}
        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Gradient
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP, mb: FIELD_MARGIN_BOTTOM }}>
          <Checkbox
            checked={isGradientEnabled}
            onChange={handleGradientToggle}
            disabled={disabled}
            size="small"
            sx={checkboxSx}
            icon={<Box sx={checkboxIconSx} />}
            checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
          />
          <Typography variant="body2" color="text.secondary">
            Enable Gradient
          </Typography>
        </Box>

        {isGradientEnabled && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Height</Typography>
                <Typography variant="caption" color="text.primary" fontWeight={500}>
                  {gradientHeight}%
                </Typography>
              </Box>
              <Slider
                value={gradientHeight}
                onChange={handleGradientHeightChange}
                min={0}
                max={100}
                step={5}
                disabled={disabled}
                size="small"
              />
            </Box>
            <StyleFieldRow
              config={withScreenEditorFieldGuide(V2_FLEXIBLE_GRADIENT_COLOR_CONFIG, 'flexibleSections')}
              value={gradientColor}
              onChange={handleGradientColorChange}
              disabled={disabled}
            />
          </Box>
        )}
      </EditorGuideProvider>

      <MultiLocaleUploadDialog
        open={showLocaleDialog}
        onClose={handleCancelLocaleDialog}
        mediaType={pendingMediaType}
        currentLocale={locale}
        availableLocales={availableLocales}
        onApplyAll={handleApplyToAllLocales}
        onApplyCurrent={handleApplyToCurrentLocale}
      />
    </SAccordion>
  );
};
